import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import { X, Sparkles, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const COOLDOWN_PREFIX = "lw_promo_cooldown_";
const SEEN_PREFIX = "lw_promo_seen_";

const REWARD_TEXT = {
  discount_percent: (v, label) => `-${v}% ${label || ""}`,
  discount_amount:  (v, label) => `-${v}€ ${label || ""}`,
  free_months:      (v, label) => `${v} ${v == 1 ? "mes gratis" : "meses gratis"} ${label || ""}`,
  extra_data:       (v, label) => `+${v}GB ${label || "extra"}`,
  free_tv:          (v, label) => `LiveWifi TV gratis ${label ? "— " + label : ""}`,
  custom:           (v, label) => label || "",
};

const AUDIENCE_TAG = {
  new_customer: "Nuevos clientes",
  existing_customer: "Para clientes",
  refer_friend: "Trae un amigo",
  rural: "Zonas rurales",
  student: "Estudiantes",
  senior: "+65 años",
  business: "Autónomos y empresas",
  all: "Para todos",
};

const computeInitialHidden = (allPromos) => {
  const next = new Set();
  for (const p of allPromos) {
    try {
      const everyMin = Number(p.show_every_minutes || 0);
      if (everyMin > 0) {
        const last = Number(localStorage.getItem(`${COOLDOWN_PREFIX}ls_${p.id}`) || 0);
        if (last > 0 && (Date.now() - last) < everyMin * 60 * 1000) {
          next.add(p.id);
        }
      } else if (sessionStorage.getItem(`${SEEN_PREFIX}${p.id}`) === "1") {
        next.add(p.id);
      }
    } catch { /* noop */ }
  }
  return next;
};

const matchesPath = (p, pathname) => {
  const tps = p.target_paths || [];
  if (tps.length === 0) return true;
  return tps.some(tp => {
    if (!tp) return false;
    if (tp === "/") return pathname === "/";
    return pathname === tp || pathname.startsWith(tp.endsWith("/") ? tp : tp + "/");
  });
};

const BannerInner = ({ allPromos, pathname }) => {
  const [idx, setIdx] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [hiddenIds, setHiddenIds] = useState(() => computeInitialHidden(allPromos));

  const promos = allPromos
    .filter(p => matchesPath(p, pathname))
    .filter(p => !hiddenIds.has(p.id));

  // Track impression once per promo per session
  useEffect(() => {
    if (hidden) return;
    if (!promos.length) return;
    const promo = promos[idx];
    if (!promo) return;
    const seenKey = `${SEEN_PREFIX}${promo.id}`;
    try {
      if (sessionStorage.getItem(seenKey) === "1") return;
      sessionStorage.setItem(seenKey, "1");
    } catch { return; }
    axios.post(`${API}/promotions/track`, { promo_id: promo.id, event: "impression" }).catch(() => {});
  }, [promos, idx, hidden]);

  const trackEvent = (promo, event) => {
    if (!promo?.id) return;
    axios.post(`${API}/promotions/track`, { promo_id: promo.id, event }).catch(() => {});
  };

  const markDismissed = (promo) => {
    if (!promo) return;
    try {
      const everyMin = Number(promo.show_every_minutes || 0);
      if (everyMin > 0) {
        localStorage.setItem(`${COOLDOWN_PREFIX}ls_${promo.id}`, String(Date.now()));
      }
      sessionStorage.setItem(`${SEEN_PREFIX}${promo.id}`, "1");
    } catch { /* noop */ }
    setHiddenIds(prev => {
      const n = new Set(prev);
      n.add(promo.id);
      return n;
    });
  };

  const close = () => {
    const promo = promos[idx];
    if (promo) {
      trackEvent(promo, "dismiss");
      markDismissed(promo);
    }
    setHidden(true);
  };

  if (hidden || promos.length === 0) return null;

  const promo = promos[idx];
  if (!promo) return null;
  const accent = promo.banner_color || "#7DD500";
  const reward = (REWARD_TEXT[promo.reward_type] || REWARD_TEXT.custom)(promo.reward_value, promo.reward_label);
  const audience = AUDIENCE_TAG[promo.audience] || "";

  const prev = () => setIdx(i => (i - 1 + promos.length) % promos.length);
  const next = () => setIdx(i => (i + 1) % promos.length);
  const handleCta = () => {
    trackEvent(promo, "click");
    markDismissed(promo);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[110] flex justify-center px-3 pointer-events-none"
      data-testid="promotions-banner"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0F0F0F]/95 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto"
        style={{
          boxShadow: `0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px ${accent}25`,
        }}
      >
        <div
          className="absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: accent }}
        />
        <div
          className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ backgroundColor: accent }}
        />

        <div className="relative px-5 py-4 sm:px-6 sm:py-5">
          <button
            onClick={close}
            data-testid="promotions-banner-close"
            aria-label="Cerrar promociones"
            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>

          <div className="flex items-start gap-4 pr-8">
            <div
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${accent}20`, color: accent }}
            >
              <Sparkles className="w-5 h-5" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {audience && (
                  <span
                    className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${accent}20`, color: accent }}
                  >
                    {audience.toUpperCase()}
                  </span>
                )}
                {promo.code && (
                  <code className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-white/5 text-white/70">
                    {promo.code}
                  </code>
                )}
              </div>

              <h3 className="text-white font-black text-base sm:text-lg leading-tight mb-1">
                {promo.banner_title || promo.name}
              </h3>

              {(promo.banner_subtitle || reward) && (
                <p className="text-white/60 text-xs sm:text-sm leading-snug mb-3">
                  {promo.banner_subtitle ? (
                    <>
                      {promo.banner_subtitle}{" "}
                      {reward && <strong style={{ color: accent }}>{reward}</strong>}
                    </>
                  ) : (
                    <strong style={{ color: accent }}>{reward}</strong>
                  )}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {promo.banner_cta_label && (
                  promo.banner_cta_url?.startsWith("http") ? (
                    <a
                      href={promo.banner_cta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleCta}
                      data-testid="promotions-banner-cta"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black text-black transition-all hover:opacity-90"
                      style={{ backgroundColor: accent }}
                    >
                      {promo.banner_cta_label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={promo.banner_cta_url || "/"}
                      onClick={handleCta}
                      data-testid="promotions-banner-cta"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black text-black transition-all hover:opacity-90"
                      style={{ backgroundColor: accent }}
                    >
                      {promo.banner_cta_label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )
                )}

                {promos.length > 1 && (
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      onClick={prev}
                      className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60"
                      aria-label="Promo anterior"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white/40 text-[10px] font-mono">{idx + 1}/{promos.length}</span>
                    <button
                      onClick={next}
                      className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60"
                      aria-label="Promo siguiente"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PromotionsBanner = () => {
  const location = useLocation();
  const [allPromos, setAllPromos] = useState([]);
  const fetchedRef = useRef(false);

  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (fetchedRef.current || isAdminRoute) return;
    fetchedRef.current = true;
    let mounted = true;
    axios.get(`${API}/promotions/banner`)
      .then(r => { if (mounted) setAllPromos(r.data.promotions || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [isAdminRoute]);

  if (isAdminRoute) return null;
  if (!allPromos.length) return null;

  // Re-mount on pathname change so internal state (hidden/idx/hiddenIds) resets naturally
  return (
    <BannerInner
      key={location.pathname}
      allPromos={allPromos}
      pathname={location.pathname}
    />
  );
};

export default PromotionsBanner;
